import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { stepTemplateKeys } from '../query-keys';
import {
  createStepTemplateContract,
  updateStepTemplateContract,
  deleteStepTemplateContract,
  type CreateStepTemplateRequest,
  type CreateStepTemplateResponse,
  type UpdateStepTemplateRequest,
  type UpdateStepTemplateResponse,
  type DeleteStepTemplateResponse
} from '@bliks-io/contracts';

/** Snapshot the selected draft nodes into a new Schrittvorlage. */
export function useCreateStepTemplate(processId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreateStepTemplateRequest): Promise<CreateStepTemplateResponse> => {
      const { data } = await apiClient.post<CreateStepTemplateResponse>(
        createStepTemplateContract.path.replace(':processId', processId),
        body
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stepTemplateKeys.list() });
    }
  });
}

/** Rename a Schrittvorlage or edit its description. */
export function useUpdateStepTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      body
    }: {
      id: string;
      body: UpdateStepTemplateRequest;
    }): Promise<UpdateStepTemplateResponse> => {
      const { data } = await apiClient.patch<UpdateStepTemplateResponse>(
        updateStepTemplateContract.path.replace(':id', id),
        body
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stepTemplateKeys.list() });
    }
  });
}

/** Soft-delete a Schrittvorlage. */
export function useDeleteStepTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<DeleteStepTemplateResponse> => {
      const { data } = await apiClient.delete<DeleteStepTemplateResponse>(
        deleteStepTemplateContract.path.replace(':id', id)
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stepTemplateKeys.list() });
    }
  });
}
